import { API_BASE_URL } from '@/environment'
import {
  toFilterQueryParams,
  toPaginationQueryParams,
} from '@/helpers/api/toQueryParams'
import apiService from '@/service/api.service'
import { apiV3 } from '@/service/v3/apiV3'
import { customFetch } from '@/service/v3/apiV3Utils'
import { get } from '@/service/v3/httpUtils'
import {
  addUserGroup as addUserGroupService,
  removeUserGroup as removeUserGroupService,
} from '@/service/v3/v3Endpoints'
import type { User } from '@/types'
import type { V3PaginatedResponse, V3Response } from '@/types/V3UtilityTypes'
import { queryParams } from '@attensi/utils'
import type {
  ApiGroup,
  CurrentUserV1,
  CurrentUserV3,
  DeleteUserNoteParams,
  GetUserGroupsParams,
  GetUserNoteParams,
  GetUserParams,
  GetUserProductBundleProgressParams,
  GetUserProductProgressParams,
  GetUserPortalStatusParams,
  PatchUserGroupsBatchParams,
  PatchUserNoteParams,
  PatchUserParams,
  PostUserActiveParams,
  PostUserInReportsParams,
  PostUserNoteParams,
  PostUserProductActivityCertificationParams,
  PostUserProductCertificationParams,
  PostUserScenarioCertificationParams,
  UserNote,
  UserPatch,
  UserPortalStatus,
  UserProductBundleProgressResponse,
  UserProductProgressResponse,
  UserProductStatus,
} from './userTypes'

// Re-export types for convenience
export type {
  DeleteUserNoteParams,
  GetUserNoteParams,
  GetUserParams,
  GetUserPortalStatusParams,
  PostUserActiveParams as PatchUserActiveParams,
  PostUserInReportsParams as PatchUserInReportsParams,
  PatchUserNoteParams,
  PatchUserParams,
  PostUserNoteParams,
  PostUserProductCertificationParams,
  PostUserScenarioCertificationParams,
  UserNote,
  UserPatch,
  UserPortalStatus,
}

const API_ADMIN_NAMESPACE = 'api/v3/admin'

export const getUserGroups = async ({
  userId,
  include,
  filter,
  pagination = { number: 1, size: 25 },
}: GetUserGroupsParams): V3PaginatedResponse<ApiGroup> => {
  // Only include 'additional' filter for position and department group types
  const shouldIncludeAdditionalFilter =
    filter?.filterGroupType === 'position' ||
    filter?.filterGroupType === 'department'

  const apiFilter = {
    name: filter?.filterName,
    groupType:
      filter?.filterGroupType === 'custom'
        ? 'default'
        : filter?.filterGroupType,
    additional: shouldIncludeAdditionalFilter
      ? filter?.filterAdditional
      : undefined,
  }

  const groupsQueryParams = {
    include,
    ...toFilterQueryParams(apiFilter),
    ...toPaginationQueryParams(pagination),
  }

  const url = queryParams(
    `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/users/${userId}/groups`,
    groupsQueryParams
  )

  const response = await customFetch(url, { method: 'GET' })
  if (!response.ok) {
    throw new Error(`Failed to fetch user groups: ${response.statusText}`)
  }
  return response.json()
}

export type DeleteUserGroupParams = {
  userId: number
  groupId: number
}

export const deleteUserGroup = async ({
  userId,
  groupId,
}: DeleteUserGroupParams) => {
  return removeUserGroupService(userId, groupId)
}

/**
 * Adds and removes user groups in parallel. Throws if all operations fail.
 */
export const patchUserGroupsBatch = async (
  variables: PatchUserGroupsBatchParams
) => {
  const promises: Promise<unknown>[] = []
  const { groupsToRemove = [], groupsToAdd = [], userId } = variables

  for (const { groupId } of groupsToRemove) {
    promises.push(removeUserGroupService(userId, groupId))
  }

  for (const groupData of groupsToAdd) {
    promises.push(addUserGroupService(userId, groupData))
  }

  const results = await Promise.allSettled(promises)

  const fulfilled = results.filter((r) => r.status === 'fulfilled')
  const rejected = results.filter((r) => r.status === 'rejected')

  if (rejected.length === groupsToAdd.length + groupsToRemove.length) {
    throw new Error('All group operations failed')
  }

  return {
    fulfilled,
    rejected,
    total: results.length,
  }
}

export const getUserProductStatus = async ({
  userId,
  productId,
}: {
  userId: string
  productId: string
}) => {
  const result = await apiService.getUserProductStatus(userId, productId)
  if (result.error) {
    throw new Error(JSON.stringify(result.error))
  }
  return result.data as UserProductStatus
}

export const getUserProductProgress = ({
  userId,
  productId,
}: GetUserProductProgressParams) =>
  get<UserProductProgressResponse>(
    `/admin/users/${userId}/products/${productId}/progress`
  )

// Bundle progress only exists outside the admin namespace; it resolves against
// the target user's own products, so it is correct for a managed user.
export const getUserProductBundleProgress = ({
  userId,
  productBundleId,
}: GetUserProductBundleProgressParams) =>
  get<UserProductBundleProgressResponse>(
    `/users/${userId}/product_bundles/${productBundleId}/progress`
  )

export const getUserPortalStatus = async ({
  userId,
}: GetUserPortalStatusParams): Promise<UserPortalStatus> => {
  const result = await apiService.getUserPortalStatus(userId)
  if (result.error) {
    throw new Error(JSON.stringify(result.error))
  }
  return result.data as UserPortalStatus
}

export const postUserProductCertification = async ({
  userId,
  productId,
  registrationId,
}: {
  userId: number
  productId: number
  registrationId: number
}) => {
  const response = await apiService.postCertifyProduct(
    userId,
    productId,
    registrationId
  )

  if (response.error) {
    throw new Error(`Failed to update user: ${response.statusText}`)
  }

  return response.data
}

export const postUserProductActivityCertification = async ({
  userId,
  productId,
}: PostUserProductActivityCertificationParams) => {
  const url = `${API_BASE_URL}/api/v3/admin/products/${productId}/users/${userId}/certify_activity`
  const response = await customFetch(url, {
    method: 'POST',
  })
  if (!response.ok) {
    throw new Error(
      `Failed to certify user activity for product: ${response.statusText}`
    )
  }
  return await response.json()
}

export const postUserScenarioCertification = async ({
  userId,
  scenarioId,
}: PostUserScenarioCertificationParams) => {
  await apiService.postCertifyScenario(userId, scenarioId)
}

export const getUser = async ({ userId }: GetUserParams) => {
  return await (get(`/admin/users/${userId}`) as V3Response<User>)
}

export const getUserV1 = async ({ userId }: GetUserParams): Promise<User> => {
  const result = await apiService.getUserById(userId)
  if (result.error) {
    throw new Error(JSON.stringify(result.error))
  }
  return result.data as User
}

export const getCurrentUserV1 = async (): Promise<CurrentUserV1> => {
  const result = await apiService.getCurrentUser()
  if (result.error) {
    throw new Error(JSON.stringify(result.statusText))
  }
  return result.data as CurrentUserV1
}

export const getCurrentUserV3 = async (): Promise<CurrentUserV3> => {
  return (await apiV3.users.getCurrentUser()) as CurrentUserV3
}

export const patchUser = async ({ userId, fields }: PatchUserParams) => {
  const url = `${API_BASE_URL}/api/v3/admin/users/${userId}`

  const response = await customFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({ data: { attributes: { ...fields } } }),
  })

  if (!response.ok) {
    throw new Error(`Failed to update user: ${response.statusText}`)
  }

  return await response.json()
}

export const postUserActiveToggle = async ({
  userId,
  activate,
}: PostUserActiveParams): Promise<void> => {
  const url = `${API_BASE_URL}/api/v3/admin/users/${userId}/activate`

  const response = await customFetch(url, {
    method: activate ? 'POST' : 'DELETE',
  })

  if (!response.ok) {
    throw new Error(
      `Failed to toggle user active status: ${response.statusText}`
    )
  }
}

export const postUserInReportsToggle = async ({
  userId,
}: PostUserInReportsParams) => {
  const result = await apiService.toggleUserReportStatus(userId)
  if (result.error) {
    throw new Error(result.statusText || 'Failed to toggle user report status')
  }
  return result.data
}

export const getUserNote = async ({
  userId,
}: GetUserNoteParams): V3Response<UserNote> => {
  const url = `${API_BASE_URL}/api/v3/admin/users/${userId}/note`

  const response = await customFetch(url, {
    method: 'GET',
  })

  if (response.status === 404) {
    return undefined
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch user note: ${response.statusText}`)
  }

  return await response.json()
}

export const patchUserNote = async ({ userId, note }: PatchUserNoteParams) => {
  const url = `${API_BASE_URL}/api/v3/admin/users/${userId}/note`

  const response = await customFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        attributes: {
          note,
        },
      },
    }),
  })

  if (!response.ok) {
    throw new Error(`Failed to update user note: ${response.statusText}`)
  }

  return await response.json()
}
